import type { ResultRendererProps, ToolCore } from "@loumy/contracts";
import { ALL_TOOL_CORES } from "@loumy/tools";
import type { ComponentType } from "react";

// §3.5/§4 Frontend-Tool-Registry. „Nichts referenziert ein Tool hart": Shell, Tabs und
// Result-Viewer gehen ausschließlich hier durch. ToolCore EAGER (für buildPipeline/
// Pricing/Defaults), ToolUI LAZY (`() => import('../tools/<id>/ui')`, D16/D11) — ein
// Tool = ein Ordner + eine Zeile in UI_LOADERS (§5). Der `spritesheet`-Slot ist real
// registriert; sein Generator ist (Provider/Feasibility-Gate offen, §13/D24) nur
// strukturell, nicht funktional verdrahtet.

/**
 * Settings-Form-Props (§3.4 SettingsUIProps, hier konkret auf den losen Draft-Record
 * fixiert — der Submit parst ihn gegen das Zod-Schema, SSOT). Ein einheitlicher
 * Props-Typ für alle SettingsUIs = keine Generics-/Varianz-Reibung im Registry-Store.
 */
export interface SettingsFormProps {
  value: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  /** D101: im aktuellen Modell versteckte Settings-Keys (hiddenSettingsFor) — handgebaute
   *  SettingsUIs blenden diese Felder aus. Rein UI: der Server strippt die Werte ohnehin. */
  hiddenSettings?: ReadonlySet<string>;
}

/** D124/D132 — Ausführungs-Modus eines Tools im Web-UI. */
export type ToolMode = "submit" | "browser-edit";

/** D132 — Struktur-Subset der uiStore-Inputs (je Rolle), auf dem eine mode-Funktion entscheidet. */
export type ToolModeInputs = Record<string, { asset: { kind: string } }[]> | undefined;

/** D124/D132 — effektiver Modus: statischer Wert, Funktions-Auswertung oder Default `submit`. */
export function resolveToolMode(ui: WebToolUI | undefined, inputs: ToolModeInputs): ToolMode {
  if (!ui?.mode) return "submit";
  return typeof ui.mode === "function" ? ui.mode(inputs) : ui.mode;
}

/** Web-ToolUI: echte React-Komponenten über dem §3.4-Vertrag (ToolUI). */
export interface WebToolUI {
  readonly id: string;
  readonly name: string;
  readonly icon: { name: string };
  /**
   * D124 — Ausführungs-Modus im Web-UI: `"submit"` (Default) = Job-Pfad (Generieren-Button,
   * Credits-Vorschau); `"browser-edit"` = das Tool rechnet LIVE im Browser (Pyodide/ops.py),
   * der Standard-Submit-Block entfällt — `FooterUI` (falls gesetzt) ersetzt ihn. Der
   * Job-Pfad des Tools bleibt für die Public-API unverändert (D51). Registry-getrieben,
   * kein Tool-Hardcoding in der Shell (§1/§4).
   *
   * D132 — alternativ eine FUNKTION der aktuellen Tool-Inputs (je Rolle): Tools mit
   * Media-Union-Slots entscheiden modalitäts-abhängig (chroma-key-remover: Bild/leer =
   * `browser-edit` LIVE, Video = `submit`-Job). Die Entscheidung lebt im Tool-Modul,
   * die Shell bleibt generisch (sie ruft nur `resolveToolMode`).
   */
  readonly mode?: ToolMode | ((inputs: ToolModeInputs) => ToolMode);
  readonly FooterUI?: ComponentType<Record<string, never>>;
  readonly SettingsUI: ComponentType<SettingsFormProps>;
  readonly ResultRenderer: ComponentType<ResultRendererProps>;
}

type UiModule = { default: WebToolUI };
type UiLoader = () => Promise<UiModule>;

// Die EINE Stelle, an der Tools angedockt werden (§5). Reihenfolge = Tab-Reihenfolge.
const UI_LOADERS: Record<string, UiLoader> = {
  spritesheet: () => import("../tools/spritesheet/ui"),
  "image-generator": () => import("../tools/image-generator/ui"),
  "chroma-key-remover": () => import("../tools/chroma-key-remover/ui"),
  "bg-remove": () => import("../tools/bg-remove/ui"), // D146: Bria-AI-Matting (Einzelbild)
  "image-upscale": () => import("../tools/image-upscale/ui"), // D163: Real-ESRGAN-Super-Resolution (Einzelbild)
};

const coresById = new Map<string, ToolCore>(ALL_TOOL_CORES.map((c) => [c.id, c]));
const uiCache = new Map<string, Promise<WebToolUI | undefined>>();

/**
 * Sichtbare Tools = die MIT UI-Tab (§5). Interne Cores (D139 studio-export: server-only
 * WebM-Export-Job, kein Tab) bleiben aus Tabs/Suche/Kategorien raus — sind aber via `getCore`
 * für Pricing/Submit (estimateCredits, Studio-WebM) erreichbar. Spiegelt die D51-Logik (ein
 * Tool ohne UI_LOADERS-Eintrag wird im Frontend nicht angeboten).
 */
export function listCores(category?: string): ToolCore[] {
  const all = [...coresById.values()].filter((c) => c.id in UI_LOADERS);
  return category ? all.filter((c) => c.category === category) : all;
}

export function getCore(id: string): ToolCore | undefined {
  return coresById.get(id);
}

/** Distinkte Kategorien in stabiler Insertion-Order der SICHTBAREN (UI-gestützten) Cores. */
export function listCategories(): string[] {
  const seen: string[] = [];
  for (const c of coresById.values()) if (c.id in UI_LOADERS && !seen.includes(c.category)) seen.push(c.category);
  return seen;
}

/** Lazy ToolUI laden (gecacht). Kein registrierter Loader → undefined → kind-Fallback (D32/D43). */
export function loadToolUI(id: string): Promise<WebToolUI | undefined> {
  const cached = uiCache.get(id);
  if (cached) return cached;
  const loader = UI_LOADERS[id];
  const p: Promise<WebToolUI | undefined> = loader ? loader().then((m) => m.default) : Promise.resolve(undefined);
  uiCache.set(id, p);
  return p;
}
